import { ArchiveBoxIcon, CheckCircleIcon, EyeIcon, TrashIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Badge, BadgeProps, Column, Dialog, Table } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  ApiReportTemplateStatusChoices,
  ReportTemplatesDocument,
} from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";
import { useCursorPagination } from "@hooks/useCursorPagination";
import { ConfirmArchiveDialog } from "@pages/Templates/components/Dialog/ConfirmArchiveDialog";
import { ConfirmPublishDialog } from "@pages/Templates/components/Dialog/ConfirmPublishDialog";
import { useReportTemplateActions } from "@pages/Templates/hooks/templates";

type ColumnValue = string | boolean | null | undefined;

export type ReportTemplate = {
  id: string;
  name: string;
  reportType: ApiReportTemplateReportTypeChoices;
  createdDate: string;
  lastUpdatedDate: string;
  status: ApiReportTemplateStatusChoices;
  isBaseTemplate: boolean;
  description?: string | null;
};

export const BadgeSettingFromStatus: { [key in ApiReportTemplateStatusChoices]: BadgeProps["appearance"] } = {
  PUBLISHED: "success",
  DRAFT: "neutral",
  ARCHIVED: "error",
};

interface TemplatesTableProps {
  statuses: ApiReportTemplateStatusChoices[];
}

export const TemplatesTable: React.FC<TemplatesTableProps> = ({ statuses }) => {
  const go = useNavigate();

  const pageItemsDefault = 20;
  const [searchTerm, setSearchTerm] = useState<string>("");

  // add debounce to delay querying until user stops typing
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const { data, isFetching, getCurrentPageData, totalItemCount, refetch } = useCursorPagination({
    queryKey: "get-report-templates",
    queryVariables: {
      filter: {
        searchTerm: debouncedSearchTerm,
        statuses: statuses,
      },
    },
    document: ReportTemplatesDocument,
    responseKey: "getReportTemplates",
    pageSize: pageItemsDefault,
    watchedVariables: [debouncedSearchTerm],
  });

  const templates = data || [];

  const columns: Column<ReportTemplate>[] = [
    {
      dataIndex: "name",
      key: "name",
      title: "Title",
      className: "text-stone-900",
      render: (value: ColumnValue, record: ReportTemplate) => (
        <div className="flex items-center gap-x-2">
          <span className="text-stone-900">{value as string}</span>
          {record?.isBaseTemplate && (
            <span className="inline-flex max-h-6 min-w-fit items-center rounded-md px-2 py-1 text-xs font-medium bg-blue-100 text-blue-600">
              Frontera
            </span>
          )}
        </div>
      ),
    },
    {
      dataIndex: "reportType",
      key: "reportType",
      title: "Type",
      render: (value: ColumnValue) => (value ? convertReadableString(value as string) : ""),
      className: "text-stone-500",
    },
    {
      dataIndex: "createdDate",
      key: "createdDate",
      title: "Created",
      className: "text-stone-500",
      render: (value: ColumnValue) => (value ? format(new Date(value as string), "MMM dd yyyy") : ""),
    },
    {
      dataIndex: "lastUpdatedDate",
      key: "lastUpdatedDate",
      title: "Last Updated",
      className: "text-stone-500",
      render: (value: ColumnValue) => (value ? format(new Date(value as string), "MMM dd yyyy") : ""),
    },
    {
      dataIndex: "status",
      key: "status",
      title: "Status",
      render: (value: ColumnValue, record: ReportTemplate) => (
        <div className="min-w-36">
          <Badge appearance={BadgeSettingFromStatus[record.status]} text={convertReadableString(value as string)} />
        </div>
      ),
    },
  ];

  const {
    selectedTemplate,
    setSelectedTemplate,
    archiveDialogOpen,
    setArchiveDialogOpen,
    archiveTemplate,
    publishDialogOpen,
    setPublishDialogOpen,
    publishTemplate,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteSelectedTemplate,
  } = useReportTemplateActions({ refetch });

  const getActionsForTemplate = (template: ReportTemplate) => {
    const view = {
      label: "View",
      Icon: EyeIcon,
      callback: (template: ReportTemplate) => go(`/templates/${template.id}`),
    };
    const publish = {
      label: "Publish",
      Icon: CheckCircleIcon,
      callback: (template: ReportTemplate) => {
        setSelectedTemplate(template);
        setPublishDialogOpen(true);
      },
    };
    const deleteTemplate = {
      label: "Delete",
      textColorClassName: "text-red-700",
      Icon: TrashIcon,
      callback: (template: ReportTemplate) => {
        setSelectedTemplate(template);
        setDeleteDialogOpen(true);
      },
    };
    const archive = {
      label: "Archive",
      textColorClassName: "text-red-700",
      Icon: ArchiveBoxIcon,
      callback: (template: ReportTemplate) => {
        setSelectedTemplate(template);
        setArchiveDialogOpen(true);
      },
    };

    if (template.isBaseTemplate) {
      return [view];
    }

    switch (template.status) {
      case ApiReportTemplateStatusChoices.Draft:
        return [view, publish, deleteTemplate];

      case ApiReportTemplateStatusChoices.Published:
        return [view, archive];

      case ApiReportTemplateStatusChoices.Archived:
        return [view, publish];

      default:
        return [view];
    }
  };

  return (
    <div className="mt-4 flow-root bg-white w-full">
      <Table
        onRowClick={(template) => {
          go(`/templates/${template.id}`);
        }}
        defaultItemsPerPage={pageItemsDefault}
        showPagination
        loading={isFetching}
        showSearch
        inputClassName="w-96"
        itemsPerPageOptions={[5, 10, 20, 50]}
        actions={getActionsForTemplate}
        data={templates}
        columns={columns}
        onPageChange={getCurrentPageData}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        totalItemCount={totalItemCount}
      />
      <Dialog
        title={`Confirm deletion of ${selectedTemplate?.name}?`}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        primaryButton={{
          text: "Confirm",
          onClick: deleteSelectedTemplate,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setSelectedTemplate(null);
            setDeleteDialogOpen(false);
          },
        }}
        size="sm"
      />
      <ConfirmPublishDialog
        open={publishDialogOpen}
        setOpen={setPublishDialogOpen}
        onConfirm={publishTemplate}
        onCancel={() => {
          setSelectedTemplate(null);
          setPublishDialogOpen(false);
        }}
        templateName={selectedTemplate?.name || ""}
        templateDescription={selectedTemplate?.description || ""}
      />
      <ConfirmArchiveDialog
        open={archiveDialogOpen}
        setOpen={setArchiveDialogOpen}
        onConfirm={archiveTemplate}
        onCancel={() => {
          setSelectedTemplate(null);
          setArchiveDialogOpen(false);
        }}
        templateName={selectedTemplate?.name || ""}
      />
    </div>
  );
};
